import {
  VuexModule,
  Module,
  Action,
  Mutation
} from 'vuex-module-decorators'

import { clearLegacyAuthCookies } from '@/utils/cookies'
import { tokenStore } from '@/utils/tokenStore'

import { AuthPayload, FetchMeQuery, LogoutQuery, RefreshAccessTokenQuery, UserRole } from '@/types/graphql'
import { createProvider } from '@/vue-apollo'
import { setScreenLoading } from '@/utils/spinner'
import { ApolloQueryResult } from 'apollo-client'
import * as Sentry from '@sentry/vue'

export type UserState = {
  accessToken: string
  name: string
  email: string
  roles: UserRole[]
  contextDepartments: FetchMeQuery['me']['contextDepartments']
  departments?: FetchMeQuery['me']['departments']
}

@Module({ name: 'User', namespaced: true })
export default class User extends VuexModule implements UserState {
  // ➊ State
  // accessToken is in-memory only: a truthy value means "session alive". On
  // reload it starts empty and restoreSession() re-fills it from the HttpOnly
  // refresh cookie.
  private userState: UserState = {
    accessToken: '',
    name: '',
    email: '',
    roles: [],
    contextDepartments: [],
    departments: []
  }

  // ➋ Getters
  public get accessToken(): string {
    return this.userState.accessToken
  }

  public get name(): string {
    return this.userState.name
  }

  public get email() {
    return this.userState.email
  }

  public get roles() {
    return this.userState.roles
  }

  public get isAuthenticated() {
    return this.accessToken
  }

  public get contextDepartments() {
    return this.userState.contextDepartments
  }

  public get departmentList() {
    return this.userState.departments
  }

  // ➌ Mutations
  @Mutation
  private SET_TOKEN(token: string) {
    this.userState.accessToken = token
  }

  @Mutation
  private SET_NAME(name: string) {
    this.userState.name = name
  }

  @Mutation
  private SET_EMAIL(email: string) {
    this.userState.email = email
  }

  @Mutation
  private SET_ROLES(roles: UserRole[]) {
    this.userState.roles = roles
  }

  @Mutation
  private SET_CONTEXT_DEPARTMENTS(contextDepartment: FetchMeQuery['me']['contextDepartments']) {
    this.userState.contextDepartments = contextDepartment
  }

  @Mutation
  private SET_DEPARTMENT_LIST(departmentList: FetchMeQuery['me']['departments']) {
    this.userState.departments = departmentList
  }

  // ➍ Actions
  @Action
  public setUserData(payload: AuthPayload) {
    const {
      access_token,
      expires_in,
      refresh_token,
      user
    } = payload

    if (
      !access_token ||
          !expires_in ||
          !refresh_token ||
          !user
    ) {
      throw new Error('Invalid auth payload!')
    }

    // Session transport is the HttpOnly cookie pair set by the backend; the
    // token is only held in memory (Echo channel auth + route guard).
    tokenStore.set(access_token)

    this.SET_TOKEN(access_token)
    this.SET_NAME(user.name)
    this.SET_EMAIL(user.email)

    if (user.roles && Array.isArray(user.roles)) {
      this.SET_ROLES(user.roles.map((role) => { return role?.name as UserRole }))
    }
  }

  @Action
  public async loadUser() {
    try {
      const result: ApolloQueryResult<FetchMeQuery> = await createProvider()
        .defaultClient
        .query({ query: FetchMeQuery })

      const { me } = result.data

      this.SET_NAME(me.name)
      this.SET_EMAIL(me.email)

      this.SET_CONTEXT_DEPARTMENTS(me.contextDepartments)

      if (me.departments !== undefined && Array.isArray(me.departments)) {
        this.SET_DEPARTMENT_LIST(me.departments)
      }

      Sentry.setUser({ email: result.data.me.email })
    } catch (error) {
      console.error(error)
    }
  }

  @Action
  public async restoreSession() {
    // The session lives in an HttpOnly cookie JS cannot read; ask the backend
    // for a fresh access token (the browser sends the refresh cookie along).
    try {
      const res = await createProvider().defaultClient.mutate({
        mutation: RefreshAccessTokenQuery,
        variables: { input: {} }
      })

      const accessToken = res.data?.refreshToken?.access_token

      if (accessToken) {
        tokenStore.set(accessToken)
        this.SET_TOKEN(accessToken)
      }
    } catch {
      // No valid session cookie; the user logs in normally.
    }
  }

  @Action
  public async logOut() {
    setScreenLoading(true)

    // JS cannot delete the HttpOnly cookies; the backend clears them in the
    // logout response (and revokes the token). Only called with a live session
    // (logOut also runs on the login page when the session is already dead).
    if (tokenStore.get()) {
      try {
        await createProvider().defaultClient.mutate({ mutation: LogoutQuery })
      } catch (error) {
        console.error(error)
      }
    }

    tokenStore.clear()
    clearLegacyAuthCookies()

    this.SET_TOKEN('')
    this.SET_NAME('')
    this.SET_EMAIL('')
    this.SET_ROLES([])
    this.SET_CONTEXT_DEPARTMENTS(null)
    this.SET_DEPARTMENT_LIST([])
  }
}
